import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Route,
  AlertTriangle,
  FileSpreadsheet,
  Printer,
  User,
  Info,
  Calendar,
  Wallet,
  Truck,
  DollarSign,
} from 'lucide-react';
import {
  fetchShippingRunsheets,
  getRunsheetExportUrl,
  type ShippingRunsheetsResponse,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Stat } from '../ui/data.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd } from '../lib/format.js';

const MONTHS = [
  { value: 'all', label: 'Tous les mois' },
  { value: '1', label: '01 · Janvier' },
  { value: '2', label: '02 · Février' },
  { value: '3', label: '03 · Mars' },
  { value: '4', label: '04 · Avril' },
  { value: '5', label: '05 · Mai' },
  { value: '6', label: '06 · Juin' },
  { value: '7', label: '07 · Juillet' },
  { value: '8', label: '08 · Août' },
  { value: '9', label: '09 · Septembre' },
  { value: '10', label: '10 · Octobre' },
  { value: '11', label: '11 · Novembre' },
  { value: '12', label: '12 · Décembre' },
] as const;

/**
 * S41 — Feuilles de Route & Tournées Livreurs (Middleware Shiparon)
 *
 * One row per middleware tour (idRunsheet).
 * Filters by Livreur, Year (2023-2026), Month (Jan-Dec), Agency, and Status.
 * Computes financial breakdown per runsheet: Total COD, Frais de livraison, Montant MaPara.
 * Color-codes unclosed / en cours runsheets in bold red.
 * Exports to Excel (.xlsx) and printable PDF with full financial details.
 */
export function LogisticsRunsheetsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [livreur, setLivreur] = useState('all');
  const [year, setYear] = useState<'all' | '2026' | '2025' | '2024' | '2023'>('all');
  const [month, setMonth] = useState('all');
  const [unitFee, setUnitFee] = useState<number | undefined>(undefined);
  const [agence, setAgence] = useState('all');
  const [status, setStatus] = useState<'all' | 'validated' | 'in_progress'>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const runsheetsQuery = useQuery({
    queryKey: ['shipping', 'runsheets', query, livreur, year, month, agence, status, unitFee, page, limit],
    queryFn: () =>
      fetchShippingRunsheets({
        search: query.trim() || undefined,
        livreur: livreur === 'all' ? undefined : livreur,
        year: year === 'all' ? undefined : year,
        month: month === 'all' ? undefined : month,
        unitFee,
        agence: agence === 'all' ? undefined : agence,
        status,
        page,
        limit,
      }),
    placeholderData: (prev) => prev,
  });

  const data: ShippingRunsheetsResponse | undefined = runsheetsQuery.data;
  const tours = data?.tours ?? [];
  const totalTours = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const summary = data?.summary;
  const livreurs = data?.livreurs ?? [];

  // KPIs across the entire filtered dataset (from backend summary)
  const totalPackages = summary ? summary.totalPackages : tours.reduce((acc, t) => acc + t.packageCount, 0);
  const totalCod = summary ? summary.totalCod : tours.reduce((acc, t) => acc + t.totalCod, 0);
  const totalDelivered = summary ? summary.totalDelivered : tours.reduce((acc, t) => acc + t.deliveredCount, 0);
  const totalDeliveryFee = summary ? summary.totalDeliveryFee : tours.reduce((acc, t) => acc + t.deliveryFee, 0);
  const totalNetAmount = summary ? summary.totalNetAmount : tours.reduce((acc, t) => acc + t.netAmount, 0);
  const totalValidated = summary ? summary.totalValidated : tours.filter((t) => t.validated).length;
  const totalInProgress = summary ? summary.totalInProgress : tours.filter((t) => !t.validated).length;
  const validatedPct = totalTours > 0 ? Math.round((totalValidated / totalTours) * 100) : 0;
  const deliveryPct = totalPackages > 0 ? Math.round((totalDelivered / totalPackages) * 100) : 0;

  const selectedLivreurObj = livreurs.find((l) => l.id === livreur);
  const livreurDisplayName = selectedLivreurObj ? selectedLivreurObj.nom : 'Tous les livreurs';

  const exportUrl = getRunsheetExportUrl({
    livreur,
    year,
    month,
    unitFee,
    agence,
    status,
    search: query.trim() || undefined,
  });

  const monthLabel = MONTHS.find((m) => m.value === month)?.label ?? 'Tous les mois';
  const periodDisplay =
    year === 'all' && month === 'all'
      ? 'Toutes les dates'
      : year !== 'all' && month !== 'all'
        ? `${monthLabel} ${year}`
        : year !== 'all'
          ? `Année ${year}`
          : `Mois ${monthLabel}`;

  return (
    <div className="space-y-6 animate-fade">
      <style>{`
        @media print {
          body { background: white !important; color: black !important; font-size: 10px !important; }
          header, nav, aside, .no-print, button, input, select { display: none !important; }
          .print-header { display: block !important; margin-bottom: 14px; border-bottom: 2px solid #065f46; padding-bottom: 8px; }
          .print-badge-encours { color: #991b1b !important; font-weight: bold !important; }
          .print-badge-valid { color: #065f46 !important; font-weight: bold !important; }
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #d1d5db !important; padding: 4px 6px !important; }
          .en-cours-row { background-color: #fee2e2 !important; color: #991b1b !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: landscape; margin: 8mm; }
        }
        .print-header { display: none; }
      `}</style>

      {/* Printable Report Header */}
      <div className="print-header">
        <h1 className="text-xl font-bold text-ink">MaPara.tn · Synthèse des Feuilles de Route (Shiparon)</h1>
        <p className="text-xs text-ink-soft">
          Livreur : <strong>{livreurDisplayName}</strong> · Période : <strong>{periodDisplay}</strong> · Statut : <strong>{status === 'all' ? 'Tous (validés & en cours)' : status === 'validated' ? 'Validés uniquement' : 'En cours uniquement'}</strong> · Date export : {new Date().toLocaleDateString('fr-FR')}
        </p>
        <p className="text-xs font-semibold text-ink mt-1">
          Tournées : {totalTours.toLocaleString('fr-FR')} ({totalValidated} validées / {totalInProgress} en cours) · Colis livrés : {totalDelivered} / {totalPackages} ({deliveryPct} %) · Total COD : {formatTnd(totalCod)} · Frais Livraison : {formatTnd(totalDeliveryFee)} · <strong>Montant Net MaPara : {formatTnd(totalNetAmount)}</strong>
        </p>
      </div>

      <PageHeader
        index="S41"
        eyebrow="Gestion Logistique · Tournées Livreurs"
        title="Feuilles de Route & Tournées (Shiparon)"
        purpose="Historique des tournées du middleware Shiparon : filtrez par livreur, année et mois, suivez le Total COD, les frais de livraison et le montant net MaPara par tournée. Les tournées non clôturées (en cours) sont signalées en rouge."
        meta={[
          { label: 'Tournées', value: totalTours.toLocaleString('fr-FR') },
          { label: 'En cours (non clôturées)', value: totalInProgress.toLocaleString('fr-FR') },
          { label: 'Validées (clôturées)', value: totalValidated.toLocaleString('fr-FR') },
          { label: 'Montant Net MaPara', value: formatTnd(totalNetAmount) },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2 no-print">
            <a
              href={exportUrl}
              download
              className="inline-flex items-center justify-center rounded-lg border border-herb-700 bg-herb-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-herb-800 transition-colors"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
              Exporter Excel (.xlsx)
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />
              Exporter PDF / Imprimer
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void runsheetsQuery.refetch()}
              disabled={runsheetsQuery.isFetching}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${runsheetsQuery.isFetching ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        }
      />

      {/* Year 2023 notice banner if user selected 2023 */}
      {year === '2023' && totalTours === 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-3 text-xs text-sky-900 no-print">
          <Info className="h-4 w-4 shrink-0 text-sky-700 mt-0.5" />
          <div className="space-y-1">
            <strong className="font-semibold text-sky-950">
              Information Middleware Shiparon (Période 2023) :
            </strong>
            <p>
              L'instance Shiparon a été déployée et mise en production le <strong>22 janvier 2024</strong> (Runsheet genesis N° 1).
              La base Shiparon ne contient aucun enregistrement antérieur à 2024.
              Sélectionnez <strong>2024</strong>, <strong>2025</strong> ou <strong>2026</strong> pour consulter et exporter l'historique de vos livreurs.
            </p>
          </div>
        </div>
      )}

      {/* Synthèse Livreur / Summary Banner when a specific courier is selected */}
      {selectedLivreurObj ? (
        <div className="rounded-xl border border-herb-200 bg-gradient-to-r from-herb-50/80 via-white to-herb-50/50 p-4 shadow-sm no-print">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-3 border-b border-herb-100">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-herb-700 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                {selectedLivreurObj.nom.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-ink">{selectedLivreurObj.nom}</h2>
                  {selectedLivreurObj.matricule && (
                    <span className="font-mono text-xs px-2 py-0.5 rounded bg-herb-100 text-herb-800 font-semibold">
                      {selectedLivreurObj.matricule}
                    </span>
                  )}
                  <span className="text-xs text-ink-soft">
                    · {totalTours} tournée(s) ({totalValidated} validée(s), {totalInProgress} en cours)
                  </span>
                </div>
                <p className="text-xs text-ink-soft mt-0.5">
                  Période : <strong>{periodDisplay}</strong>
                  {' · '}Taux de livraison : <strong>{deliveryPct} %</strong> ({totalDelivered.toLocaleString('fr-FR')} / {totalPackages.toLocaleString('fr-FR')} colis)
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-white border border-border rounded-lg px-2.5 py-1 text-xs shadow-2xs">
                <span className="text-ink-soft">Tarif unitaire livraison :</span>
                <input
                  type="number"
                  step="0.05"
                  placeholder="7.140"
                  value={unitFee !== undefined ? unitFee : ''}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setUnitFee(Number.isNaN(v) || v <= 0 ? undefined : v);
                  }}
                  className="w-16 font-mono font-semibold text-ink outline-none text-right"
                />
                <span className="text-ink-soft">DT</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3">
            <div className="rounded-lg bg-white border border-border p-3 shadow-2xs">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-ink-soft uppercase tracking-wider">Total Encaissé (COD)</span>
                <Wallet className="h-4 w-4 text-ink-faint" />
              </div>
              <div className="text-lg font-bold font-mono text-ink mt-0.5">{formatTnd(totalCod)}</div>
              <span className="text-[11px] text-ink-faint">Fonds bruts perçus par le livreur</span>
            </div>

            <div className="rounded-lg bg-white border border-rose-100 p-3 shadow-2xs">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-rose-700 uppercase tracking-wider">Frais de Livraison</span>
                <Truck className="h-4 w-4 text-rose-500" />
              </div>
              <div className="text-lg font-bold font-mono text-rose-700 mt-0.5">{formatTnd(totalDeliveryFee)}</div>
              <span className="text-[11px] text-rose-600/80">
                {totalDelivered > 0 ? `${totalDelivered.toLocaleString('fr-FR')} colis livrés` : `${totalPackages.toLocaleString('fr-FR')} colis`} × {unitFee ? unitFee.toFixed(3) : '7.140'} DT
              </span>
            </div>

            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 shadow-2xs">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-emerald-800 uppercase tracking-wider">Montant Net MaPara</span>
                <DollarSign className="h-4 w-4 text-emerald-700" />
              </div>
              <div className="text-xl font-black font-mono text-emerald-800 mt-0.5">{formatTnd(totalNetAmount)}</div>
              <span className="text-[11px] text-emerald-700 font-semibold">Net à reverser à MaPara (COD − Frais)</span>
            </div>
          </div>
        </div>
      ) : (
        /* Global KPI strip when all couriers are selected */
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat
            label="Tournées"
            value={totalTours.toLocaleString('fr-FR')}
            hint={`${totalValidated} validée(s) · ${totalInProgress} en cours`}
          />
          <Stat
            label="Colis & Taux"
            value={`${totalDelivered.toLocaleString('fr-FR')} / ${totalPackages.toLocaleString('fr-FR')}`}
            hint={`Taux de livraison : ${deliveryPct} %`}
          />
          <Stat
            label="Total COD"
            value={formatTnd(totalCod, '0,000')}
            hint="Total des fonds collectés"
          />
          <Stat
            label="Frais de Livraison"
            value={formatTnd(totalDeliveryFee, '0,000')}
            hint="Rémunération transporteurs"
          />
          <Stat
            label="Montant Net MaPara"
            value={formatTnd(totalNetAmount, '0,000')}
            tone="good"
            hint="Fonds nets dus à MaPara"
          />
        </div>
      )}

      {/* Unassigned scan rows alert */}
      {(data?.unassignedCount ?? 0) > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 no-print">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong>{data?.unassignedCount?.toLocaleString('fr-FR')}</strong> scan(s) sans N° de
            tournée dans Shiparon — consultables dans le rapprochement logistique.
          </span>
        </div>
      )}

      {/* Interactive Filters Bar */}
      <Panel className="no-print">
        <div className="p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search Input */}
            <form
              className="flex items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                setQuery(search);
                setPage(1);
              }}
            >
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="N° tournée, matricule, agence…"
                className="h-8.5 w-56 rounded-lg border border-border bg-card px-3 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 outline-none"
              />
              <Button type="submit" variant="outline" size="sm">
                <Search className="h-3.5 w-3.5 mr-1" />
                Chercher
              </Button>
            </form>

            {/* Livreur Dropdown Selector */}
            <div className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-ink-soft shrink-0" />
              <select
                value={livreur}
                onChange={(event) => {
                  setLivreur(event.target.value);
                  setPage(1);
                }}
                className="h-8.5 max-w-[210px] rounded-lg border border-border bg-card px-2 text-xs text-ink font-medium focus:border-herb-600 outline-none"
              >
                <option value="all">Tous les livreurs ({livreurs.length})</option>
                {livreurs.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.nom} {l.matricule ? `(${l.matricule})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Year Selector Tabs */}
            <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-0.5">
              {(['all', '2026', '2025', '2024', '2023'] as const).map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => {
                    setYear(y);
                    setPage(1);
                  }}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    year === y ? 'bg-herb-700 text-card shadow-sm' : 'text-ink-soft hover:bg-cream'
                  }`}
                >
                  {y === 'all' ? 'Toutes années' : y}
                </button>
              ))}
            </div>

            {/* Month Dropdown Selector */}
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-ink-soft shrink-0" />
              <select
                value={month}
                onChange={(event) => {
                  setMonth(event.target.value);
                  setPage(1);
                }}
                className="h-8.5 rounded-lg border border-border bg-card px-2 text-xs text-ink font-medium focus:border-herb-600 outline-none"
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Agence Dropdown */}
            <select
              value={agence}
              onChange={(event) => {
                setAgence(event.target.value);
                setPage(1);
              }}
              className="h-8.5 rounded-lg border border-border bg-card px-2 text-xs text-ink"
            >
              <option value="all">Toutes les agences</option>
              {(data?.agences ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>

            {/* Status Filter Tabs */}
            <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-0.5">
              {(
                [
                  ['all', 'Tous statuts'],
                  ['validated', 'Clôturées (Validées)'],
                  ['in_progress', 'En cours (Non clôturées)'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setStatus(value);
                    setPage(1);
                  }}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    status === value
                      ? value === 'in_progress'
                        ? 'bg-rose-700 text-white shadow-sm'
                        : 'bg-herb-700 text-card shadow-sm'
                      : 'text-ink-soft hover:bg-cream'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Page Size Selector */}
            <select
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
              className="h-8.5 rounded-lg border border-border bg-card px-2 text-xs text-ink ml-auto"
            >
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
        </div>
      </Panel>

      {/* Runsheet Tours Table */}
      <Panel>
        {runsheetsQuery.isLoading ? (
          <div className="py-16 text-center">
            <Loading label="Chargement des tournées Shiparon…" />
          </div>
        ) : tours.length === 0 ? (
          <div className="py-16 text-center text-sm text-ink-soft">
            Aucune feuille de route trouvée avec ces filtres.
          </div>
        ) : (
          <div className="p-3">
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th>N° Runsheet</Th>
                    <Th>Date</Th>
                    <Th>Livreur</Th>
                    <Th>Agence</Th>
                    <Th align="right">Colis</Th>
                    <Th align="right">Total COD</Th>
                    <Th align="right">Frais Livr.</Th>
                    <Th align="right">Montant MaPara</Th>
                    <Th>Statut</Th>
                  </Tr>
                </THead>
                <TBody>
                  {tours.map((t) => {
                    const isEnCours = !t.validated;
                    return (
                      <Tr
                        key={t.idRunsheet}
                        className={`cursor-pointer transition-colors ${
                          isEnCours
                            ? 'bg-rose-50/70 hover:bg-rose-100/80 border-l-4 border-l-rose-500 en-cours-row'
                            : 'hover:bg-cream/40'
                        }`}
                        onClick={() => navigate(`/shipping/runsheets/${t.idRunsheet}`)}
                      >
                        <Td>
                          <span
                            className={`inline-flex items-center gap-1.5 font-mono text-xs font-bold ${
                              isEnCours ? 'text-rose-800' : 'text-ink'
                            }`}
                          >
                            <Route
                              className={`h-3.5 w-3.5 ${isEnCours ? 'text-rose-600' : 'text-herb-700'}`}
                            />
                            #{t.idRunsheet}
                          </span>
                        </Td>
                        <Td>
                          <span className="text-xs text-ink-soft">
                            {t.tourDate ? new Date(t.tourDate).toLocaleDateString('fr-FR') : '—'}
                          </span>
                        </Td>
                        <Td>
                          <div
                            className={`text-xs font-semibold ${
                              isEnCours ? 'text-rose-950' : 'text-ink'
                            }`}
                          >
                            {t.livreurNom ?? '—'}
                          </div>
                          {t.matricule && (
                            <div className="font-mono text-[10px] text-ink-faint">{t.matricule}</div>
                          )}
                        </Td>
                        <Td>
                          <span className="text-xs text-ink-soft">{t.agence ?? '—'}</span>
                        </Td>
                        <Td align="right">
                          <span
                            className={`font-mono text-xs font-semibold ${
                              isEnCours ? 'text-rose-900' : 'text-ink'
                            }`}
                          >
                            {t.deliveredCount} / {t.packageCount}
                          </span>
                        </Td>
                        <Td align="right">
                          <span
                            className={`font-mono text-xs font-bold ${
                              isEnCours ? 'text-rose-900' : 'text-ink'
                            }`}
                          >
                            {formatTnd(t.totalCod)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span
                            className="font-mono text-xs font-semibold text-rose-700"
                            title={`Tarif: ${t.unitFee.toFixed(3)} DT / colis`}
                          >
                            {formatTnd(t.deliveryFee)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span
                            className={`font-mono text-xs font-black ${
                              isEnCours ? 'text-rose-800' : 'text-emerald-800'
                            }`}
                          >
                            {formatTnd(t.netAmount)}
                          </span>
                        </Td>
                        <Td>
                          {t.validated ? (
                            <span className="inline-flex items-center text-[10px] font-semibold bg-emerald-50 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200 print-badge-valid">
                              Clôturé (Validé)
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-[10px] font-bold bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded-full border border-rose-300 shadow-xs print-badge-encours">
                              En cours (Non clôturé)
                            </span>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </TableShell>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-border text-xs text-ink-soft no-print">
                <span>
                  Page <strong>{data?.page}</strong> sur <strong>{totalPages}</strong> (
                  {totalTours.toLocaleString('fr-FR')} tournées)
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Suivant <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
